import { Fragment, useState } from "react";
import { HelpOutlineOutlined as HelpOutlineOutlinedIcon } from "@mui/icons-material";
import { Box, Button, Grid, Link, Typography } from "@mui/material";
import {
  blue,
  grey,
  lightGreen,
  orange,
  purple,
  red,
  teal,
  yellow,
} from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartjsTooltip,
  Colors,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
} from "chart.js";
import { Bar, Line, Pie } from "react-chartjs-2";
import Attribute from "./Attribute";
import Tooltip from "../Tooltip";
import { useIsPrint } from "../PrintOnly";
import APIWebsiteTraffic from "../../types/APIWebsiteTraffic";
import { formatMonthYearLabel } from "../../utilities/formatting";

ChartJS.register(
  ChartjsTooltip,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Legend,
  ArcElement,
  Colors
);

function NoTraffic() {
  const theme = useTheme();
  return (
    <Typography variant="caption" sx={{ color: theme.palette.grey[500] }}>
      No traffic
    </Typography>
  );
}

const DEFAULT_NUM_KEYWORDS_TO_SHOW = 10;

function KeywordsAttribute({
  label,
  keywords,
  description,
  hasTraffic,
  mb,
}: {
  label: string;
  // `paid` arrives as null on the wire even though the schema says the field is
  // just absent, so this has to tolerate both.
  keywords: string[] | null | undefined;
  description: string | null | undefined;
  hasTraffic: boolean;
  mb?: number;
}) {
  const isPrint = useIsPrint();
  // The PDF is rendered with print media emulated, so the report gets the whole
  // list rather than a silently truncated one.
  const [showMore, setShowMore] = useState(isPrint);

  // A different searchId on the same route does not remount this. Browser back
  // lands straight on the previous result -- LookupDomain replaces the
  // intermediate `/?run=1` entry -- and LookupCompany leaves the previous
  // companyLookup in state while the next one loads, so its Outlet never
  // unmounts. Without this reset the next company's list inherits the last
  // one's expansion.
  //
  // Adjusted during render rather than in an effect, which would cascade a
  // second render and trip the repo's setState-in-effect lint. Compares by
  // reference, so the call sites must keep passing the parsed API array
  // straight through -- building a fresh array there would reset on every
  // render and make the toggle unclickable.
  const [renderedKeywords, setRenderedKeywords] = useState(keywords);
  if (keywords !== renderedKeywords) {
    setRenderedKeywords(keywords);
    setShowMore(isPrint);
  }

  const hiddenCount = (keywords?.length ?? 0) - DEFAULT_NUM_KEYWORDS_TO_SHOW;

  return (
    <Attribute
      label={label}
      labelIcon={
        <Tooltip title={description} placement="right" arrow>
          <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
        </Tooltip>
      }
    >
      {/*
        As with `measurement` below, the empty case has to collapse to null in
        this expression -- the one `Attribute` receives as children. An element
        is never falsy, not even one that renders null, so pushing the check
        down into a child component would swallow the "Not Found" fallback.
      */}
      {!hasTraffic ? (
        <NoTraffic />
      ) : keywords?.length ? (
        <Box sx={{ mt: 1, mb }}>
          {keywords
            .slice(0, showMore ? undefined : DEFAULT_NUM_KEYWORDS_TO_SHOW)
            .map((keyword, i) => (
              // keywords repeat, so the keyword alone is not a unique key
              <Box key={`${keyword}-${i}`} sx={{ mt: 0.5 }}>
                <Typography style={{ wordWrap: "break-word" }}>
                  "{keyword}"
                </Typography>
              </Box>
            ))}
          {hiddenCount > 0 && !isPrint && (
            <Button
              size="small"
              onClick={() => setShowMore((prev) => !prev)}
              sx={{ mt: 0.5, px: 0, minWidth: 0 }}
            >
              {showMore ? "Show Less" : `Show ${hiddenCount} More`}
            </Button>
          )}
        </Box>
      ) : null}
    </Attribute>
  );
}

// `Attribute` only falls back to its "Not Found" text when its children are
// falsy, and an array holding a `false` never is, so a missing measurement has
// to collapse all the way down to null.
function measurement<T>(
  value: T | null | undefined,
  format: (value: T) => string
) {
  return value === null || value === undefined ? null : format(value);
}

const singlePercentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const compactNumberFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact" as const,
  compactDisplay: "short" as const,
});

const sourcesPieOptions = {
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "right" as const,
    },
    tooltip: {
      callbacks: {
        title: function () {
          return "";
        },
        label: function (context: { label: string }) {
          return context.label;
        },
      },
    },
  },
};

const historyLineOptions = {
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        title: function () {
          return "";
        },
        label: function (context: { raw: unknown; label: string }) {
          const num =
            typeof context.raw === "number"
              ? context.raw.toLocaleString("en-US")
              : context.raw;
          return `${context.label}: ${num}`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      ticks: {
        callback: function (value: string | number) {
          const num = typeof value === "string" ? parseInt(value) : value;
          return compactNumberFormatter.format(num);
        },
      },
    },
  },
};

const countriesBarOptions = {
  maintainAspectRatio: false,
  indexAxis: "y" as const,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        title: function () {
          return "";
        },
        label: function (context: { label: string }) {
          return context.label;
        },
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        callback: function (value: string | number) {
          const num = typeof value === "string" ? parseInt(value) : value;
          return new Intl.NumberFormat("en-US", {
            style: "percent" as const,
          }).format(num);
        },
      },
    },
  },
};

function AttributesContainer({
  websiteTraffic,
  websiteTrafficProperties,
  itemSizes,
}: {
  websiteTraffic: APIWebsiteTraffic | null | undefined;
  websiteTrafficProperties: any | null | undefined;
  itemSizes: number[];
}) {
  return (
    <Grid
      container
      spacing={1}
      columnSpacing={{ xs: 1, md: 6 }}
      rowSpacing={{ xs: 1, md: 0 }}
    >
      <Grid
        size={{
          xs: itemSizes[0],
          sm: itemSizes[1],
          md: itemSizes[2],
          lg: itemSizes[3],
        }}
        sx={{
          marginBottom: 2,
        }}
      >
        <Attribute
          label="Visits per Month"
          labelIcon={
            <Tooltip
              title={
                websiteTrafficProperties &&
                websiteTrafficProperties.visits_per_month.description
              }
              placement="right"
              arrow
            >
              <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
            </Tooltip>
          }
        >
          {websiteTraffic ? (
            measurement(websiteTraffic.visits_per_month, (visits) =>
              visits.toLocaleString("en-US")
            )
          ) : (
            <NoTraffic />
          )}
        </Attribute>
      </Grid>

      <Grid
        size={{
          xs: itemSizes[0],
          sm: itemSizes[1],
          md: itemSizes[2],
          lg: itemSizes[3],
        }}
        sx={{
          marginBottom: 2,
        }}
      >
        <Attribute
          label="Time on Site"
          labelIcon={
            <Tooltip
              title={
                websiteTrafficProperties &&
                websiteTrafficProperties.time_on_site.description
              }
              placement="right"
              arrow
            >
              <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
            </Tooltip>
          }
        >
          {websiteTraffic ? (
            measurement(
              websiteTraffic.time_on_site,
              (seconds) => seconds.toFixed(0) + "s"
            )
          ) : (
            <NoTraffic />
          )}
        </Attribute>
      </Grid>

      <Grid
        size={{
          xs: itemSizes[0],
          sm: itemSizes[1],
          md: itemSizes[2],
          lg: itemSizes[3],
        }}
        sx={{
          marginBottom: 2,
        }}
      >
        <Attribute
          label="Pages per Visit"
          labelIcon={
            <Tooltip
              title={
                websiteTrafficProperties &&
                websiteTrafficProperties.pages_per_visit.description
              }
              placement="right"
              arrow
            >
              <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
            </Tooltip>
          }
        >
          {websiteTraffic ? (
            measurement(websiteTraffic.pages_per_visit, (pages) =>
              pages.toFixed(1)
            )
          ) : (
            <NoTraffic />
          )}
        </Attribute>
      </Grid>

      <Grid
        size={{
          xs: itemSizes[0],
          sm: itemSizes[1],
          md: itemSizes[2],
          lg: itemSizes[3],
        }}
        sx={{
          marginBottom: 2,
        }}
      >
        <Attribute
          label="Bounce Rate"
          labelIcon={
            <Tooltip
              title={
                websiteTrafficProperties &&
                websiteTrafficProperties.bounce_rate.description
              }
              placement="right"
              arrow
            >
              <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
            </Tooltip>
          }
        >
          {websiteTraffic ? (
            measurement(websiteTraffic.bounce_rate, (rate) =>
              new Intl.NumberFormat("en-US", { style: "percent" }).format(rate)
            )
          ) : (
            <NoTraffic />
          )}
        </Attribute>
      </Grid>
      <Grid
        size={{
          xs: itemSizes[0],
          sm: itemSizes[1],
          md: itemSizes[2],
          lg: itemSizes[3],
        }}
        sx={{
          marginBottom: 2,
        }}
      >
        <Attribute
          label="Google Results Count"
          labelIcon={
            <Tooltip
              title={
                websiteTrafficProperties &&
                websiteTrafficProperties.google_results_count.description
              }
              placement="right"
              arrow
            >
              <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
            </Tooltip>
          }
        >
          {websiteTraffic ? (
            measurement(websiteTraffic.google_results_count, (results) =>
              results.toLocaleString("en-US")
            )
          ) : (
            <NoTraffic />
          )}
        </Attribute>
      </Grid>

      <Grid
        size={{
          xs: itemSizes[0],
          sm: itemSizes[1],
          md: itemSizes[2],
          lg: itemSizes[3],
        }}
        sx={{
          marginBottom: 2,
        }}
      >
        <Attribute
          label="Paid Search Visits"
          labelIcon={
            <Tooltip
              title={
                websiteTrafficProperties &&
                websiteTrafficProperties.paid_search_visits.description
              }
              placement="right"
              arrow
            >
              <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
            </Tooltip>
          }
        >
          {websiteTraffic ? (
            measurement(websiteTraffic.paid_search_visits, (visits) =>
              visits.toLocaleString("en-US")
            )
          ) : (
            <NoTraffic />
          )}
        </Attribute>
      </Grid>

      <Grid
        size={{
          xs: 12,
          sm: 12,
          md: Math.max(itemSizes[3], 8),
          lg: Math.max(itemSizes[3], 8),
        }}
        sx={{
          marginBottom: 2,
        }}
      >
        <Attribute
          label="Top referring websites"
          labelIcon={
            <Tooltip
              title={
                websiteTrafficProperties &&
                websiteTrafficProperties.top_referring_sites.description
              }
              placement="right"
              arrow
            >
              <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
            </Tooltip>
          }
        >
          {!websiteTraffic ? (
            <NoTraffic />
          ) : websiteTraffic.top_referring_sites?.length ? (
            websiteTraffic.top_referring_sites.map((topRef) => {
              return (
                <Typography key={topRef.url}>
                  {Math.round(topRef.traffic_share * 100)}%<span> - </span>
                  <Link
                    href={topRef.url}
                    sx={{ ml: 0.25 }}
                    target="_blank"
                    rel="noopener"
                  >
                    {/* allow long urls to break at the . */}
                    {(topRef.url || "")
                      .split(".")
                      .map((part, i) => (
                        <Fragment key={`${part}${i}`}>
                          {part}&#8203;{/*zero-width space*/}
                        </Fragment>
                      ))
                      // "intersperse", but shitty, cuz js
                      // https://stackoverflow.com/questions/37128624/terse-way-to-intersperse-element-between-all-elements-in-javascript-array
                      .flatMap((e, i) => [
                        <Fragment key={`dot${i}`}>.</Fragment>,
                        e,
                      ])
                      .slice(1)}
                  </Link>
                </Typography>
              );
            })
          ) : null}
        </Attribute>
      </Grid>
    </Grid>
  );
}

export default function WebsiteTraffic({
  websiteTraffic,
  websiteTrafficProperties,
}: {
  websiteTraffic: APIWebsiteTraffic | null | undefined;
  websiteTrafficProperties: any | null | undefined;
}) {
  const theme = useTheme();

  if (websiteTraffic && !websiteTraffic.visits_per_month)
    return (
      <Grid
        size={{
          xs: 12,
          lg: 4,
        }}
      >
        <AttributesContainer
          websiteTraffic={websiteTraffic}
          websiteTrafficProperties={websiteTrafficProperties}
          itemSizes={[12, 6, 3, 3]}
        />
      </Grid>
    );

  const topCountries = websiteTraffic?.top_country_shares
    ? websiteTraffic.top_country_shares.slice(0, 6)
    : [];

  const countriesBarData =
    topCountries.length > 0
      ? {
          labels: topCountries.map(({ name, abbr, value }) => {
            const label = `${
              name.length > 20 ? abbr : name
            }: ${singlePercentFormatter.format(value)}`;

            return label;
          }),
          datasets: [
            {
              data: topCountries.map((countryShare) => countryShare.value),
              backgroundColor: topCountries.map(
                (_, i) =>
                  [teal[800], teal[600], teal[400], teal[200], teal[50]][i]
              ),
            },
          ],
        }
      : undefined;

  const sourcesPieData = websiteTraffic?.source_shares && {
    labels: Object.entries(websiteTraffic.source_shares).map(
      ([key, value], i) => `${key}: ${singlePercentFormatter.format(value)}`
    ),
    datasets: [
      {
        data: Object.values(websiteTraffic.source_shares),
        backgroundColor: [
          red[800],
          yellow[800],
          purple[800],
          blue[800],
          lightGreen[800],
          orange[800],
        ],
      },
    ],
  };

  const historyMonths = websiteTraffic?.historical_visits_per_month
    ? websiteTraffic.historical_visits_per_month.slice(0, 7)
    : [];

  // both keyword lists document themselves off the same schema property
  const keywordsDescription =
    websiteTrafficProperties &&
    websiteTrafficProperties.top_keywords.description;

  const historyLineData =
    historyMonths.length > 2
      ? {
          labels: historyMonths.map((monthVisits) =>
            formatMonthYearLabel(monthVisits.year, monthVisits.month)
          ),
          datasets: [
            {
              data: historyMonths.map((monthVisits) => monthVisits.visits),
              borderColor: theme.palette.primary.main,
              tension: 0.2,
            },
          ],
        }
      : undefined;

  return (
    <Grid
      container
      spacing={2}
      columnSpacing={{ xs: 2, md: 6 }}
      rowSpacing={{ xs: 2, md: 0 }}
    >
      <Grid
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        {(!websiteTraffic || historyLineData) && (
          <Grid size={12}>
            <Typography
              sx={{
                color: grey[600],
                fontSize: 14,
                fontWeight: 600,
                marginTop: 0,
                marginBottom: 0,
              }}
            >
              Monthly Visits History{!websiteTraffic && " (No traffic found)"}
              <Tooltip
                title={
                  websiteTrafficProperties &&
                  websiteTrafficProperties.historical_visits_per_month
                    .description
                }
                placement="right"
                arrow
              >
                <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
              </Tooltip>
            </Typography>
            <Box
              sx={{
                position: "relative",
                height: "150px",
                marginBottom: 3,
              }}
            >
              {historyLineData && (
                <Line data={historyLineData} options={historyLineOptions} />
              )}
            </Box>
          </Grid>
        )}

        {(!websiteTraffic || countriesBarData) && (
          <Grid size={12}>
            <Typography
              sx={{
                color: grey[600],
                fontSize: 14,
                fontWeight: 600,
                marginTop: 0,
                marginBottom: 0,
              }}
            >
              Top Countries Share {!websiteTraffic && "(No traffic found)"}
              <Tooltip
                title={
                  websiteTrafficProperties &&
                  websiteTrafficProperties.top_country_shares.description
                }
                placement="right"
                arrow
              >
                <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
              </Tooltip>
            </Typography>
            <Box
              sx={{
                position: "relative",
                height: "150px",
                marginBottom: 3,
              }}
            >
              <Bar
                data={
                  countriesBarData || {
                    labels: [],
                    datasets: [
                      {
                        data: [],
                        borderColor: theme.palette.primary.main,
                      },
                    ],
                  }
                }
                options={countriesBarOptions}
              />
            </Box>
          </Grid>
        )}

        {sourcesPieData && (
          <Grid size={12}>
            <Typography
              sx={{
                color: grey[600],
                fontSize: 14,
                fontWeight: 600,
                marginTop: 0,
                marginBottom: 0,
              }}
            >
              Traffic Sources
              <Tooltip
                title={
                  websiteTrafficProperties &&
                  websiteTrafficProperties.source_shares.description
                }
                placement="right"
                arrow
              >
                <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
              </Tooltip>
            </Typography>
            <Box
              sx={{
                position: "relative",
                height: "150px",
                marginBottom: 3,
              }}
            >
              <Pie data={sourcesPieData} options={sourcesPieOptions} />
            </Box>
          </Grid>
        )}
        {!websiteTraffic && (
          <Grid size={12}>
            <Typography
              sx={{
                color: grey[600],
                fontSize: 14,
                fontWeight: 600,
                marginTop: 0,
                marginBottom: 0,
              }}
            >
              No Traffic Sources Found
              <Tooltip
                title={
                  websiteTrafficProperties &&
                  websiteTrafficProperties.source_shares.description
                }
                placement="right"
                arrow
              >
                <HelpOutlineOutlinedIcon fontSize="inherit" sx={{ ml: 0.25 }} />
              </Tooltip>
            </Typography>
          </Grid>
        )}
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 2,
        }}
      >
        <AttributesContainer
          websiteTraffic={websiteTraffic}
          websiteTrafficProperties={websiteTrafficProperties}
          itemSizes={[12, 6, 6, 12]}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 2,
        }}
      >
        <KeywordsAttribute
          label="Top Organic Keywords"
          keywords={websiteTraffic?.top_keywords?.organic}
          description={keywordsDescription}
          hasTraffic={!!websiteTraffic}
          // separates the two labels; paid deliberately has no spacer
          mb={5}
        />
        <KeywordsAttribute
          label="Top Paid Keywords"
          keywords={websiteTraffic?.top_keywords?.paid}
          description={keywordsDescription}
          hasTraffic={!!websiteTraffic}
        />
      </Grid>
    </Grid>
  );
}
